export const en: Record<string, unknown> = {
  meta: {
    title: 'TimeLock — time-locked file encryption',
    description: 'Encrypt files that can only be decrypted after a chosen time. Everything in your browser.',
  },
  tabs: {
    encrypt: 'Encrypt',
    decrypt: 'Decrypt',
  },
  footer: {
    link: 'How does this work? Why is this safe?',
  },
  encrypt: {
    step1: 'Choose a file',
    step2: 'Choose a policy',
    step3: 'Set the time',
    step4: 'Encrypt',
    dropzone: 'Drag a file here or click to browse',
    large_file: 'Large file — encryption may take a moment',
    policy_time: 'Time',
    policy_time_manual: 'Time + manual',
    policy_manual: 'Manual only',
    desc_time: 'The decryption key becomes available automatically at the chosen time. Nobody — not even you — can decrypt earlier.',
    desc_manual: 'The key is only released when you do so yourself using the key file. No time component.',
    desc_time_manual: 'The key becomes available at the chosen time, or earlier if you release it manually.',
    btn_encrypt: 'Encrypt file',
    encrypting: 'Encrypting…',
    err_no_file: 'Choose a file first.',
    err_invalid_time: 'Invalid time.',
    err_future_time: 'Choose a time in the future.',
    err_unknown: 'Unknown error',
    confirm_time: 'Encrypted. The file can only be opened at the chosen time.',
    confirm_manual: 'Encrypted. The file can only be released using the key file.',
    confirm_time_manual: 'Encrypted. The file opens at the chosen time, or earlier if you release it.',
    key_meta_manual: 'Only way to release',
    key_meta_time_manual: 'Keep safe — for early release',
    btn_reset: 'Start over',
    btn_goto_decrypt: 'View in Decrypt →',
    aria_encrypted_file: 'Encrypted file {name}',
    aria_key_file: 'Key file {name}',
    title_drag: 'Drag to your Downloads folder or click the arrow',
    title_share: 'Share',
    title_save: 'Save',
  },
  decrypt: {
    import_title: 'Import files',
    dropzone: 'Drag .tlck or .tlck-key files here or click to browse',
    help: 'A <strong>.tlck</strong> file is queued for decryption. A <strong>.tlck-key</strong> file releases the lock so the recipient can decrypt.',
    import_error: '{name} is not a valid .tlck file',
    queue_title: 'Queue',
    queue_empty: 'No files imported yet.',
    policy_time: 'Time',
    policy_time_manual: 'Time + manual',
    policy_manual: 'Manual only',
    badge_waiting_manual: 'Waiting for manual release',
    btn_decrypt: 'Decrypt',
    decrypting: 'Decrypting…',
    decrypted: 'Decrypted',
    decrypt_failed: 'Decryption failed',
    btn_retry: 'Try again',
    title_download_tlck: 'Re-download .tlck',
    title_remove: 'Remove from queue',
    aria_remove: 'Remove',
    aria_decrypted_file: 'Decrypted file {name}',
    title_drag: 'Drag to your Downloads folder or click the arrow',
    title_share: 'Share',
    title_save: 'Save',
    release_invalid_key: 'Invalid key file.',
    release_success: 'Lock released! The file can now be decrypted.',
    release_failed: 'Release failed. Check the key file and try again.',
    release_confirm: '<strong>{name}</strong> — This is a key file. Do you want to release this lock? After release, anyone with the .tlck file can decrypt.',
    release_btn_confirm: 'Yes, release',
    release_btn_cancel: 'Cancel',
    release_working: 'Releasing…',
  },
  errors: {
    file_too_small: 'File too small',
    file_corrupted: 'File corrupted',
    no_valid_preamble: 'No valid preamble',
    no_separator: 'No separator found',
    header_unreadable: 'Header unreadable',
    unknown_version: 'Unknown version',
    no_key_available: 'No key available',
  },
  notfound: {
    title: 'Page not found',
    body: 'The page you\'re looking for doesn\'t exist. But your keys are safe!',
    back: '← Back to TimeLock',
  },
  about: {
    back: '← Back',
    title: 'How does TimeLock work?',
    section_browser_title: 'Everything in your browser',
    section_browser_body: '<p>The file you encrypt never leaves your browser in readable form. All cryptography happens locally. The server only receives what is strictly needed for manual release — and even then, never the file itself.</p>',
    section_policies_title: 'Three policies, two mechanisms',
    section_policies_body: '<ul><li><strong>Time.</strong> We use <a href="https://drand.love" target="_blank" rel="noopener">drand</a> with the <em>tlock</em> protocol. Your file is encrypted to a future drand round. Only when the drand network — a decentralised threshold consortium — publishes the corresponding signature at that moment can it be decrypted. <em>Our server plays no role and stores nothing.</em> Even we cannot open it early.</li><li><strong>Manual only.</strong> The key work is deliberately split:<ul><li>Your <strong>ownership key</strong> (ECDSA P-256) is generated in your browser. The public half goes to the server; the private half lives in the <code>.tlck-key</code> file you download.</li><li>The <strong>release key</strong> (X25519) is generated by the server. Only the public half is sent to your browser so your file can be encrypted to it. The private half never leaves the server unencrypted.</li></ul>Only after a successful challenge-response — where you sign a server-issued one-time <em>nonce</em> with your ownership key — does the server release the decryption key to the recipient.</li><li><strong>Time + manual.</strong> The file is encrypted to two recipients at once: the drand round and the server\'s release key. Whichever becomes available first — time or manual release — opens the file.</li></ul>',
    section_server_title: 'What does the server know?',
    section_server_body: '<p>As little as possible:</p><ul><li>For time-based locks: <strong>absolutely nothing</strong>. They never touch the server.</li><li>For manual locks: a random lock ID and one <em>AEAD-sealed blob</em> containing both the private release key and your public ownership key. This blob is encrypted with a key that only exists in server memory — not in the database.</li><li>No filenames, no sizes, no accounts, no IP addresses beyond what is briefly needed for rate-limiting.</li></ul>',
    section_blob_title: 'Why one sealed blob?',
    section_blob_body: '<p>An attacker who gains access to the database — but not the server processes — could otherwise swap the public ownership key and pass verification with their own signature. By sealing both keys together, with the lock ID, policy, and unlock time as <em>additional authenticated data</em>, any manipulation is cryptographically detected: a flipped byte, a blob moved between rows, or a changed policy or time — everything fails the integrity check and is rejected.</p>',
    section_integrity_title: 'File integrity',
    section_integrity_body: '<p>Every <code>.tlck</code> file carries a SHA-256 checksum. A corrupted file is detected and rejected before any decryption is attempted — saving cycles on corrupt input. Note: this is a corruption check, not authentication — the real protection against deliberate tampering comes from the inner AEAD encryption.</p>',
    section_keyfile_title: 'Keep your key file safe',
    section_keyfile_body: '<p>The <code>.tlck-key</code> is the only proof of ownership. There is deliberately no recovery procedure: lost means manual release is no longer possible. That\'s not a bug — it\'s precisely the property that ensures <em>we</em> cannot release on your behalf either.</p>',
  },
};
